// The shop's URL ↔ GET /products mapping, shared by the listing page
// (pages/LuxuryProducts.js) and the route prefetch (utils/routePrefetch.js):
// both must build the exact same first-page request so the prefetched answer
// is the one the page uses. Kept free of React and of the page's chunk.

import { priceBoundToApi } from './vat';

export const PAGE_SIZE = 24;
export const PRICE_MIN = 0;
export const RATING_CHOICES = [4, 3, 2];

const parseCsv = (value) => (value ? value.split(',').map((item) => item.trim()).filter(Boolean) : []);
const parseMaybe = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};
const parseNum = (value, fallback) => {
  // Number(null) and Number('') are 0: a missing param must keep the fallback,
  // otherwise /products?cat=bags shows a phantom "0-0 AED" filter.
  if (value === null || value === undefined || String(value).trim() === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** Every filter the URL can carry (`?cat=&sort=&sale=…`), read in one place:
 * used for the first render and whenever a link changes the query while the
 * shop is already open (header / footer / menu links to /products?…). */
export const filtersFromParams = (params) => {
  const rating = parseNum(params.get('min_rating'), 0);
  return {
    category: params.get('cat') || '',
    // AED incl. VAT, as the shopper sees prices; no max in the URL = no cap (null).
    priceRange: [Math.max(PRICE_MIN, parseNum(params.get('min_price'), PRICE_MIN)), parseMaybe(params.get('max_price'))],
    colors: parseCsv(params.get('colors')),
    sizes: parseCsv(params.get('sizes')),
    brands: parseCsv(params.get('brands')),
    inStock: params.get('in_stock') === 'true',
    onSale: params.get('sale') === 'true',
    sort: params.get('sort') || 'newest',
    minRating: RATING_CHOICES.includes(rating) ? rating : 0,
  };
};

/** GET /products filters (everything but the page) for the shop's filters. */
export const listRequestParams = (filters, searchQuery, vatRate) => {
  const params = { sort: filters.sort, with_total: true };
  if (filters.category) params.cat = filters.category;
  if (searchQuery) params.search = searchQuery;
  // The API filters on ex-VAT catalogue prices.
  if (filters.priceRange[0] > PRICE_MIN) params.min_price = priceBoundToApi(filters.priceRange[0], 'min', vatRate);
  if (filters.priceRange[1] !== null) params.max_price = priceBoundToApi(filters.priceRange[1], 'max', vatRate);
  if (filters.colors.length) params.colors = filters.colors.join(',');
  if (filters.sizes.length) params.sizes = filters.sizes.join(',');
  if (filters.brands.length) params.brands = filters.brands.join(',');
  if (filters.inStock) params.in_stock = true;
  if (filters.onSale) params.on_sale = true;
  if (filters.minRating > 0) params.min_rating = filters.minRating;
  return params;
};

/** The first page the shop requests for `searchParams` (a URLSearchParams). */
export const firstPageRequest = (searchParams, vatRate) => ({
  ...listRequestParams(filtersFromParams(searchParams), searchParams.get('search') || '', vatRate),
  limit: PAGE_SIZE,
  skip: 0,
});
